import { StandardEvents } from '@shopify/events';

/**
 * Vertical Oceans — PDP harvest/delivery date-picker calendar.
 *
 * <vo-date-picker> custom element, paired with snippets/vo-date-picker.liquid.
 * Behaviour ported from docs/calendar-implementation.md (old theme's
 * snippets/date-picker-calendar.liquid), adapted to Horizon:
 *   - the selected date is written to a hidden `properties[<name>]` input inside
 *     the product form, so Shopify submits it as a line-item property;
 *   - the Add-To-Cart button is gated (disabled) until every `[data-vo-date-property]`
 *     input is filled, with a MutationObserver that re-disables it when Horizon's
 *     product-form-component re-enables it on variant change;
 *   - a form submit interceptor is a backstop gate;
 *   - variant changes (StandardEvents.productSelect / shopify:product:select) re-render
 *     the calendar for the new SKU's dates and clear the selection.
 *
 * Three modes (driven by the JSON config block):
 *   - metafield harvest  : Fresh — property "Harvest Date", no delivery rules.
 *   - metafield delivery  : property "Delivery Date", cutoff / same-day rules apply.
 *   - all-dates           : Frozen with no metafield — every future date selectable.
 *
 * No third-party library. Source of truth = the line-item property.
 */
class VoDatePicker extends HTMLElement {
  #connected = false;

  /** Re-initialize runtime-generated calendar DOM after a cart section morph preserves
   *  this custom-element host but replaces its children with server-rendered markup. */
  refresh() {
    this.#connected = false;
    this.connectedCallback();
  }

  connectedCallback() {
    if (this.#connected) return;
    this.#connected = true;

    const configEl = this.querySelector('[data-vo-dp-config]');
    this.#config = JSON.parse(configEl?.textContent || '{}');
    const c = this.#config;

    this.#propertyName = c.propertyName || '';
    this.#dateLabel = c.dateLabel || 'date';
    this.#isDelivery = c.isDeliveryDatePicker === true;
    this.#allDates = c.allDatesAvailable === true;
    this.#isCartMode = c.cartAttributeMode === true;
    this.#harvestData = c.harvestData || null;
    this.#isSkuKeyed = !Array.isArray(this.#harvestData) && typeof this.#harvestData === 'object' && this.#harvestData !== null;
    this.#variantSkuMap = c.variantSkuMap || {};
    this.#currentSku = c.initialSku || '';
    this.#variantAvailable = c.variantAvailable !== false;
    // Shared store calendar: Moana maintains public holidays; merchants maintain
    // extra closures separately. Accept JSON arrays of ISO strings or { date, name }
    // records. Missing/malformed metafields must not break existing calendars.
    this.#unavailableDeliveryDates = new Set();
    for (const entries of [c.publicHolidays, c.blackoutDates]) {
      if (!Array.isArray(entries)) continue;
      for (const entry of entries) {
        const iso = typeof entry === 'string' ? entry : entry?.date;
        if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) continue;
        if (this.#formatISO(this.#parseISO(iso)) === iso) this.#unavailableDeliveryDates.add(iso);
      }
    }

    // Weekend scope resolution
    let skipWeekends = false;
    if (c.removeWeekends) {
      const scope = c.removeWeekendsScope;
      if (scope === 'both') skipWeekends = true;
      else if (scope === 'frozen' && c.isFrozenProduct) skipWeekends = true;
      else if (scope === 'fresh' && c.isFreshProduct) skipWeekends = true;
    }
    this.#skipWeekends = skipWeekends;

    this.#block = this;
    this.#form = this.closest('form');
    this.#hiddenInput = this.#form?.querySelector(`[data-vo-date-property="${CSS.escape(this.#propertyName)}"]`) || null;
    this.#errorEl = this.querySelector('[data-vo-dp-error]');
    this.#grid = this.querySelector('.vo-dp-grid');
    this.#monthYearEl = this.querySelector('.vo-dp-month-year');
    this.#prevBtn = this.querySelector('.vo-dp-prev');
    this.#nextBtn = this.querySelector('.vo-dp-next');

    this.#section = this.closest('.shopify-section, dialog, product-card');

    // Init view month = today (or earliest, whichever is later)
    const today = this.#today();
    this.#viewYear = today.getFullYear();
    this.#viewMonth = today.getMonth();

    // Wire nav
    this.#prevBtn?.addEventListener('click', () => this.#prevMonth());
    this.#nextBtn?.addEventListener('click', () => this.#nextMonth());

    // ATC gating + the submit backstop + the variant-change re-render are PDP-only — they
    // target the product form's Add-To-Cart button. In cart-attribute mode (cart offer)
    // there is no product form / variant, and a submit interceptor on an ancestor cart
    // form would block checkout. Skip them entirely.
    if (!this.#isCartMode) {
      this.#setupAtcGating();
      this.#form?.addEventListener('submit', this.#onSubmit, true);
      document.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    }

    // Initial render. In cart mode the initial selection (ISO "YYYY-MM-DD") comes from the
    // cart attribute via config.initialDateIso; on the PDP it comes from the hidden input
    // (display format, but starts empty so this is '' until a pick sets it to ISO).
    this.#dateMap = this.#getDateMap(this.#currentSku);
    const initialDate = c.initialDateIso || this.#hiddenInput?.value || '';
    // A cart attribute can outlive a settings change (for example, weekends may
    // have been enabled when it was saved). Never keep a restored date unless it
    // is selectable under the rules in force right now.
    this.#selectedDate = this.isDateSelectable(initialDate) ? initialDate : '';
    console.log('[vo-dp] init', {
      cartMode: this.#isCartMode,
      allDates: this.#allDates,
      isDelivery: this.#isDelivery,
      cutoffTime: this.#config.cutoffTime,
      blockSameDay: this.#config.blockSameDay,
      skipWeekends: this.#skipWeekends,
      initialDateIso: c.initialDateIso,
      hiddenInputValue: this.#hiddenInput?.value,
      today: this.#formatISO(this.#today()),
      selectedDate: this.#selectedDate,
      viewBeforeAdvance: { year: this.#viewYear, month: this.#viewMonth },
    });
    this.#advanceToFirstAvailableMonth();
    console.log('[vo-dp] init after advance', {
      viewYear: this.#viewYear,
      viewMonth: this.#viewMonth,
      selectedDate: this.#selectedDate,
    });
    this.#renderCalendar();
    if (!this.#isCartMode) this.#syncAtc();
  }

  disconnectedCallback() {
    if (!this.#isCartMode) {
      document.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
      this.#form?.removeEventListener('submit', this.#onSubmit, true);
    }
    this.#atcButtons.forEach((btn) => this.#observer?.disconnect());
  }

  /** @type {any} */ #config = {};
  /** @type {string} */ #propertyName = '';
  /** @type {string} */ #dateLabel = '';
  /** @type {boolean} */ #isDelivery = false;
  /** @type {boolean} */ #allDates = false;
  /** @type {boolean} */ #isCartMode = false;
  /** @type {any} */ #harvestData = null;
  /** @type {boolean} */ #isSkuKeyed = false;
  /** @type {Record<string,string>} */ #variantSkuMap = {};
  /** @type {string} */ #currentSku = '';
  /** @type {boolean} */ #variantAvailable = true;
  /** @type {boolean} */ #skipWeekends = false;
  /** @type {Set<string>} */ #unavailableDeliveryDates = new Set();
  /** @type {HTMLElement} */ #block = /** @type {any} */ (null);
  /** @type {HTMLFormElement | null} */ #form = null;
  /** @type {HTMLInputElement | null} */ #hiddenInput = null;
  /** @type {HTMLElement | null} */ #errorEl = null;
  /** @type {HTMLElement | null} */ #grid = null;
  /** @type {HTMLElement | null} */ #monthYearEl = null;
  /** @type {HTMLElement | null} */ #prevBtn = null;
  /** @type {HTMLElement | null} */ #nextBtn = null;
  /** @type {Element | null} */ #section = null;
  /** @type {any} */ #dateMap = null;
  /** @type {string} */ #selectedDate = '';
  /** @type {number} */ #viewYear = 0;

  /** The currently selected date as ISO "YYYY-MM-DD" (the pre-highlighted initial date
   *  until the customer clicks a cell). Exposed so the cart-offer controller can read the
   *  date the customer SEES highlighted in the card calendar when they click "Add
   *  subscription" without first clicking a day — that date (not a flat today+2) is what
   *  should flow to checkout. See [[cart-drawer-audit-findings]] C1. */
  get selectedDate() { return this.isDateSelectable(this.#selectedDate) ? this.#selectedDate : ''; }
  /** @type {number} */ #viewMonth = 0;
  /** @type {HTMLButtonElement[]} */ #atcButtons = [];
  /** @type {MutationObserver | null} */ #observer = null;
  /** @type {boolean} */ #suppressObserver = false;

  // ---- Date helpers ----
  #today() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }
  #addDays(date, n) {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
  }
  #pad(n) {
    return String(n).padStart(2, '0');
  }
  #formatISO(d) {
    return `${d.getFullYear()}-${this.#pad(d.getMonth() + 1)}-${this.#pad(d.getDate())}`;
  }
  #parseISO(iso) {
    const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
    return new Date(y, m - 1, d);
  }
  #formatDisplay(d) {
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  }

  #getDateMap(sku) {
    if (this.#allDates) return null; // null = every date available
    if (!this.#harvestData) return {};
    const entries = this.#isSkuKeyed ? (sku && this.#harvestData[sku] ? this.#harvestData[sku] : []) : this.#harvestData;
    const map = {};
    if (Array.isArray(entries)) {
      entries.forEach((entry) => {
        const available = entry && (entry.available === true || entry.available === 'true');
        map[entry.date] = { available };
      });
    }
    return map;
  }

  #computeEarliest() {
    const today = this.#today();
    let earliest = new Date(today);
    if (this.#isDelivery) {
      if (this.#config.blockSameDay) earliest = this.#addDays(earliest, 1);
      const cutoff = this.#config.cutoffTime;
      if (cutoff) {
        const [h, m] = String(cutoff).split(':').map((n) => parseInt(n, 10) || 0);
        const cutoffToday = new Date(today);
        cutoffToday.setHours(h, m, 0, 0);
        if (new Date() >= cutoffToday) earliest = this.#addDays(earliest, 1);
      }
    }
    return earliest;
  }

  /**
   * Is a given day cell non-selectable? Single source of truth for both the grid
   * render and the "any day available this month?" scan used to auto-advance.
   * @param {Date} cellDate
   * @param {Date} earliest - from #computeEarliest()
   */
  #cellDisabled(cellDate, earliest) {
    if (!this.#variantAvailable) return true;
    const iso = this.#formatISO(cellDate);
    const isWeekend = cellDate.getDay() === 0 || cellDate.getDay() === 6;
    const available = this.#allDates ? true : Boolean(this.#dateMap && this.#dateMap[iso] && this.#dateMap[iso].available);
    const past = cellDate < earliest;
    const isClosure = this.#isDelivery && this.#unavailableDeliveryDates.has(iso);
    return past || (this.#skipWeekends && isWeekend) || isClosure || !available;
  }

  /** Public validation used by non-PDP hosts before they create a cart line. */
  isDateSelectable(iso) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || '')) return false;
    const date = this.#parseISO(iso);
    // Reject rollover values such as 2026-02-31.
    if (this.#formatISO(date) !== iso) return false;
    return !this.#cellDisabled(date, this.#computeEarliest());
  }

  /** Surface the existing inline validation UI to hosts such as the cart offer. */
  showValidationError(message) {
    if (!this.#errorEl) return;
    if (message) this.#errorEl.textContent = message;
    this.#errorEl.classList.add('is-visible');
    this.#block.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Does this month contain at least one selectable day?
   */
  #hasAnyAvailableDay(year, month) {
    const earliest = this.#computeEarliest();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    let firstAvailable = null;
    for (let d = 1; d <= daysInMonth; d++) {
      if (!this.#cellDisabled(new Date(year, month, d), earliest)) { firstAvailable = d; break; }
    }
    console.log('[vo-dp] hasAnyAvailableDay', {
      year,
      month: month + 1,
      earliest: this.#formatISO(earliest),
      today: this.#formatISO(this.#today()),
      allDates: this.#allDates,
      firstAvailableDay: firstAvailable,
      result: firstAvailable !== null,
    });
    return firstAvailable !== null;
  }

  /**
   * If the month currently in view has no selectable day (e.g. today is the last day
   * of the month and every day is past the cutoff / unavailable — the whole month is
   * blocked), skip ahead to the first month that has at least one available day, so
   * the customer lands on a month they can actually pick from instead of a fully
   * blocked one. Applies on initial load and after a variant change, in every
   * calendar instance (PDP + cart offer). Capped at 12 months ahead so a product with
   * no available dates anywhere can't loop forever.
   */
  #advanceToFirstAvailableMonth() {
    // If a date is already selected (cart-attribute mode), show its month so the
    // selection stays visible instead of snapping back to today.
    if (this.#selectedDate && /^\d{4}-\d{2}-\d{2}$/.test(this.#selectedDate)) {
      const [y, m] = this.#selectedDate.split('-').map((n) => parseInt(n, 10));
      this.#viewYear = y;
      this.#viewMonth = m - 1;
    }
    for (let i = 0; i < 12; i++) {
      if (this.#hasAnyAvailableDay(this.#viewYear, this.#viewMonth)) return;
      if (this.#viewMonth === 11) { this.#viewMonth = 0; this.#viewYear += 1; }
      else this.#viewMonth += 1;
    }
  }

  // ---- Render ----
  #renderCalendar() {
    if (!this.#grid) return;
    const year = this.#viewYear;
    const month = this.#viewMonth;
    const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    console.log('[vo-dp] render', { month: monthNames[month], year, selectedDate: this.#selectedDate });
    if (this.#monthYearEl) this.#monthYearEl.textContent = `${monthNames[month]} ${year}`;

    const earliest = this.#computeEarliest();
    const todayIso = this.#formatISO(this.#today());

    this.#grid.innerHTML = '';
    ['Su','Mo','Tu','We','Th','Fr','Sa'].forEach((d) => {
      const h = document.createElement('div');
      h.className = 'vo-dp-day-header';
      h.textContent = d;
      this.#grid.appendChild(h);
    });

    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    for (let i = 0; i < firstDay; i++) {
      const e = document.createElement('div');
      e.className = 'vo-dp-cell empty';
      this.#grid.appendChild(e);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const cellDate = new Date(year, month, d);
      const iso = this.#formatISO(cellDate);
      const disabled = this.#cellDisabled(cellDate, earliest);

      const cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'vo-dp-cell' + (disabled ? ' disabled' : ' available') + (iso === this.#selectedDate ? ' selected' : '') + (iso === todayIso ? ' today' : '');
      cell.textContent = String(d);
      cell.setAttribute('data-date', iso);
      if (disabled) cell.disabled = true;
      else cell.addEventListener('click', (event) => this.#selectDate(iso, event));
      this.#grid.appendChild(cell);
    }

    this.#updateNavState();
  }

  #updateNavState() {
    // Prevent navigating before the current month (no point showing past months).
    const today = this.#today();
    const isCurrentMonth = this.#viewYear === today.getFullYear() && this.#viewMonth === today.getMonth();
    if (this.#prevBtn) this.#prevBtn.disabled = isCurrentMonth;
  }

  #prevMonth() {
    const today = this.#today();
    if (this.#viewYear === today.getFullYear() && this.#viewMonth === today.getMonth()) return;
    if (this.#viewMonth === 0) { this.#viewMonth = 11; this.#viewYear -= 1; }
    else this.#viewMonth -= 1;
    this.#renderCalendar();
  }

  #nextMonth() {
    if (this.#viewMonth === 11) { this.#viewMonth = 0; this.#viewYear += 1; }
    else this.#viewMonth += 1;
    this.#renderCalendar();
  }

  /**
   * @param {string} iso
   * @param {MouseEvent} [event] - the originating click (cell click); used to keep the
   *   cart drawer from closing on a date pick (see below).
   */
  #selectDate(iso, event) {
    // A cell click must not close the cart drawer. #renderCalendar() resets #grid.innerHTML,
    // detaching the clicked cell mid-event; the cart drawer's outside-click handler
    // (dialog.js → isClickedOutside → !dialog.contains(event.target)) would then see a
    // detached target and close the drawer. Stop the click from bubbling to the dialog, and
    // defer the re-render to a microtask so the cell stays attached through any handler that
    // still runs. Same pattern as vo-cart-drawer.js#gotoStepFromClick.
    if (!this.isDateSelectable(iso)) return;
    if (event) event.stopPropagation();
    this.#selectedDate = iso;
    if (this.#hiddenInput) {
      this.#hiddenInput.value = this.#formatDisplay(this.#parseISO(iso));
      this.#hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (this.#errorEl) this.#errorEl.classList.remove('is-visible');
    queueMicrotask(() => {
      this.#renderCalendar();
      if (!this.#isCartMode) this.#syncAtc();
    });
    this.#dispatchSelect(iso);
  }

  #clearSelection() {
    this.#selectedDate = '';
    if (this.#hiddenInput) {
      this.#hiddenInput.value = '';
      this.#hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (this.#errorEl) this.#errorEl.classList.remove('is-visible');
    this.#dispatchSelect('');
  }

  /**
   * Announce the current selection so non-PDP hosts (the cart offer) can react to a date
   * pick without coupling to the hidden line-item-property input. PDP behaviour is
   * unchanged — it ignores this event (the hidden input's `change` is still the PDP's
   * source of truth). `iso` is '' on clear.
   * @param {string} iso
   */
  #dispatchSelect(iso) {
    this.dispatchEvent(
      new CustomEvent('vo-date-picker:select', {
        bubbles: true,
        detail: { iso, display: iso ? this.#formatDisplay(this.#parseISO(iso)) : '' },
      })
    );
  }

  /**
   * Reflect an externally-selected date (ISO "YYYY-MM-DD") into the calendar WITHOUT
   * dispatching a select event (no cart round-trip). Used by the cart offer to mirror an
   * existing cart attribute into the grid on load. No-op on a non-ISO value.
   * @param {string} iso
   */
  setSelectedDate(iso) {
    if (iso && !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return;
    this.#selectedDate = this.isDateSelectable(iso) ? iso : '';
    console.log('[vo-dp] setSelectedDate called', {
      iso,
      selectedDate: this.#selectedDate,
      viewBefore: { year: this.#viewYear, month: this.#viewMonth },
    });
    // Snap the view to the selected date's month so the selection is visible (e.g.
    // the cart offer's default / saved date lands on a month the customer can
    // see). If that month has NO selectable day — e.g. a saved cart attribute
    // that is now in the past — skip ahead to the first month that does, EXACTLY
    // like the PDP's initial load (#advanceToFirstAvailableMonth). Without this a
    // stale past date pins the cart-offer calendar on a dead past month instead
    // of the current month shown on the PDP. #advanceToFirstAvailableMonth snaps
    // to #selectedDate's month when one is set, then advances; on a clear it
    // advances from the current view so it can never end on a blocked month.
    this.#advanceToFirstAvailableMonth();
    console.log('[vo-dp] setSelectedDate after advance', {
      viewYear: this.#viewYear,
      viewMonth: this.#viewMonth + 1,
      selectedDate: this.#selectedDate,
    });
    this.#renderCalendar();
  }

  // ---- ATC gating ----
  #allDatePropertiesFilled() {
    if (!this.#form) return false;
    const inputs = this.#form.querySelectorAll('[data-vo-date-property]');
    if (!inputs.length) return false;
    for (const input of inputs) if (!input.value) return false;
    return true;
  }

  #gatherAtcButtons() {
    // Only the real submit button (button[name="add"]) is gated on a selected date — it's
    // the only one that can submit the product form, so #onSubmit catches a missing date.
    // The sticky-add-to-cart button is deliberately NOT gated: it no longer adds to cart,
    // it scrolls the customer back to the variant / delivery-date selection (its whole
    // purpose when they've scrolled past the buy buttons without picking a date). Gating it
    // would disable the very "get back to the date picker" path. Its disabled state stays
    // driven by variant availability (server-rendered), not by date selection.
    const main = this.#form?.querySelector('button[name="add"]');
    return main ? [main] : [];
  }

  #setupAtcGating() {
    this.#observer?.disconnect();
    this.#atcButtons = this.#gatherAtcButtons();
    this.#observer = new MutationObserver(() => {
      if (this.#suppressObserver) return;
      // If the theme re-enabled a button while a date is still required, re-disable it.
      if (!this.#allDatePropertiesFilled() || !this.#variantAvailable) {
        this.#suppressObserver = true;
        this.#atcButtons.forEach((b) => {
          if (!b.disabled) b.disabled = true;
        });
        requestAnimationFrame(() => { this.#suppressObserver = false; });
      }
    });
    this.#atcButtons.forEach((b) => this.#observer.observe(b, { attributes: true, attributeFilter: ['disabled'] }));
  }

  #syncAtc() {
    const filled = this.#allDatePropertiesFilled();
    this.#suppressObserver = true;
    this.#atcButtons.forEach((b) => { b.disabled = !filled || !this.#variantAvailable; });
    requestAnimationFrame(() => { this.#suppressObserver = false; });
  }

  /** @param {Event} event */
  #onSubmit = (event) => {
    if (!this.#variantAvailable || !this.#allDatePropertiesFilled() || !this.isDateSelectable(this.#selectedDate)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showValidationError(`Please select an available ${this.#dateLabel.toLowerCase()} before adding to cart.`);
    }
  };

  // ---- Variant change ----
  #onProductSelect = async (event) => {
    // Ignore productSelect from quick-add product cards / other sections.
    const target = /** @type {Element | null} */ (event.target);
    if (target && this.#section && !this.#section.contains(target)) return;

    try {
      const resolved = await event.promise;
      const resource = resolved?.detail?.resource;
      if (!resource) return;
      const newSku = resource.sku || this.#variantSkuMap[resource.id] || '';
      const availabilityChanged = this.#variantAvailable !== (resource.available !== false);
      this.#variantAvailable = resource.available !== false;
      if ((newSku && newSku !== this.#currentSku) || availabilityChanged) {
        if (newSku) this.#currentSku = newSku;
        this.#dateMap = this.#getDateMap(this.#currentSku);
        this.#clearSelection();
        // Snap the view back to the current month for the new SKU's availability,
        // then skip ahead if that month is fully blocked (see #advanceToFirstAvailableMonth).
        const today = this.#today();
        this.#viewYear = today.getFullYear();
        this.#viewMonth = today.getMonth();
        this.#advanceToFirstAvailableMonth();
        this.#renderCalendar();
        this.#syncAtc();
      }
      // The theme's product-form-component morphs the ATC button area on variant
      // change; re-gather/re-observe the buttons afterwards so gating stays applied.
      requestAnimationFrame(() => {
        this.#setupAtcGating();
        this.#syncAtc();
      });
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[vo-date-picker] productSelect rejected:', error);
    }
  };
}

if (!customElements.get('vo-date-picker')) {
  customElements.define('vo-date-picker', VoDatePicker);
}
